import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Globe, AlertCircle, Loader2, Search, Filter, ArrowUpDown, MoreVertical, Check, RefreshCw, Save } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useToast } from '../../context/ToastContext';
import { Product } from '../../types';
import { supabase } from '../../utils/supabase';
import { ImageWithFallback } from '../../components/ImageWithFallback';
import { EditProductDialog } from './EditProductDialog';
import { format } from 'date-fns';

export function AdminInventory() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'name' | 'price' | 'stock_level'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    // Check if mobile and set initial view mode
    const checkMobile = () => {
      const mobile = window.innerWidth <= 640;
      setIsMobile(mobile);
      setViewMode(mobile ? 'card' : 'table');
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchProducts();

    const subscription = supabase
      .channel('products_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products' },
        (payload) => {
          console.log('Real-time product change detected:', payload);
          fetchProducts(); // Re-fetch to get freshest data
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchProducts = async () => {
    try {
      setError(null);
      setIsRefreshing(true);

      // Always order by updated_at DESC to get freshest data first
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_deleted', false) // Only fetch non-deleted products
        .order('updated_at', { ascending: false }); // Freshest data first

      if (error) throw error;
      
      // Ensure data is an array
      const productsArray = Array.isArray(data) ? data : [];
      console.log('Fetched products ordered by updated_at:', productsArray.length);
      setProducts(productsArray);
      setLastSyncTime(new Date());
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
      // Initialize empty array on error
      setProducts([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleDelete = async (productId: string) => {
    try {
      setIsDeletingProduct(productId);

      console.log('Deleting product via RPC:', productId);

      // Call the secure RPC function to delete product
      const { data, error } = await supabase
        .rpc('delete_product', {
          _id: productId
        });

      if (error) {
        console.error('RPC error deleting product:', error);
        throw new Error(error.message || 'Failed to delete product');
      }

      console.log('Product deletion successful:', data);

      // Update local state immediately
      setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
      showToast(`Product "${data.name}" deleted successfully`, 'success');
      setProductToDelete(null);

      // Re-fetch to ensure we have the latest data
      await fetchProducts();

    } catch (err) {
      console.error('Error deleting product:', err);
      showToast(`Failed to delete product: ${err.message}`, 'error');
    } finally {
      setIsDeletingProduct(null);
    }
  };

  const togglePublish = async (product: Product) => {
    try {
      setIsUpdatingStatus(product.id);
      
      const newPublishedStatus = !product.is_published;

      console.log('Updating product status via RPC:', {
        id: product.id,
        currentStatus: product.is_published,
        newStatus: newPublishedStatus,
        category: product.category
      });

      // Call the secure RPC function to update status and category
      const { data, error } = await supabase
        .rpc('update_product_meta', {
          _id: product.id,
          _category: product.category, // Keep existing category
          _published: newPublishedStatus
        });

      if (error) {
        console.error('RPC error updating status:', error);
        throw new Error(error.message || 'Failed to update product status');
      }

      console.log('Status update successful:', data);

      // Update local state immediately with confirmed values from RPC
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === product.id
            ? { 
                ...p, 
                is_published: data.is_published,
                is_draft: data.is_draft,
                updated_at: data.updated_at
              }
            : p
        )
      );

      showToast(`Product ${newPublishedStatus ? 'published' : 'unpublished'} successfully`, 'success');

      // Re-fetch products to ensure we have the latest data from Supabase
      await fetchProducts();
    } catch (err) {
      console.error('Error toggling publish status:', err);
      showToast(`Failed to update product status: ${err.message}`, 'error');
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  const saveDraft = async (product: Product) => {
    try {
      setIsUpdatingStatus(product.id);

      console.log('Saving product as draft via RPC:', product.id);

      // Call the secure RPC function to save as draft
      const { data, error } = await supabase
        .rpc('update_product_meta', {
          _id: product.id,
          _category: product.category, // Keep existing category
          _published: false
        });

      if (error) {
        console.error('RPC error saving draft:', error);
        throw new Error(error.message || 'Failed to save draft');
      }

      console.log('Draft save successful:', data);

      // Update local state immediately
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === product.id
            ? { 
                ...p, 
                is_draft: true, 
                is_published: false, 
                updated_at: data.updated_at 
              }
            : p
        )
      );

      showToast('Product saved as draft', 'success');

      // Re-fetch products to ensure we have the latest data from Supabase
      await fetchProducts();
    } catch (err) {
      console.error('Error saving draft:', err);
      showToast(`Failed to save draft: ${err.message}`, 'error');
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsEditDialogOpen(true);
  };

  const handleCardClick = (product: Product) => {
    // Only handle card click on mobile
    if (isMobile) {
      handleEdit(product);
    }
  };

  const handleSaveEdit = async (updatedProduct: Product) => {
    try {
      console.log('🔄 Product updated via server route, updating local state:', updatedProduct);

      // Update local state immediately with the updated product
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === updatedProduct.id ? updatedProduct : p
        )
      );

      showToast('Product updated successfully', 'success');
      setIsEditDialogOpen(false);
      setSelectedProduct(null);

      // Re-fetch products to ensure we have the latest data from Supabase
      await fetchProducts();
    } catch (err) {
      console.error('Error in handleSaveEdit:', err);
      showToast(`Failed to update product: ${err.message}`, 'error');
    }
  };

  // Ensure products is always an array before filtering
  const filteredProducts = products.filter(product => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(query) ||
      product.brand.toLowerCase().includes(query) ||
      product.type.toLowerCase().includes(query)
    );
  }).sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'price') {
      return (a.price - b.price) * direction;
    }
    if (sortField === 'stock_level') {
      const aStock = a.stock_level || a.stockLevel || 0;
      const bStock = b.stock_level || b.stockLevel || 0;
      return (aStock - bStock) * direction;
    }
    return a.name.localeCompare(b.name) * direction;
  });

  const ProductActions = ({ product }: { product: Product }) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className={`p-2 hover:bg-gray-100 rounded-full ${isMobile ? 'more-menu hidden' : ''}`}>
          <MoreVertical className="w-5 h-5 text-gray-600" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200 p-1"
          sideOffset={5}
        >
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100"
            onClick={() => handleEdit(product)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100"
            onClick={() => togglePublish(product)}
            disabled={isUpdatingStatus === product.id}
          >
            <Globe className="w-4 h-4 mr-2" />
            {isUpdatingStatus === product.id ? 'Updating...' : (product.is_published ? 'Unpublish' : 'Publish')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100"
            onClick={() => saveDraft(product)}
            disabled={isUpdatingStatus === product.id}
          >
            <Save className="w-4 h-4 mr-2" />
            {isUpdatingStatus === product.id ? 'Saving...' : 'Save as Draft'}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100 text-red-600"
            onClick={() => setProductToDelete(product)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-semibold">Inventory Management</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchProducts}
              disabled={isRefreshing}
              className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/admin/inventory/new')}
              className="flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </button>
          </div>
        </div>

        {/* Last sync time */}
        <div className="text-sm text-gray-500">
          Last synced: {format(lastSyncTime, 'MMM d, yyyy HH:mm:ss')} (ordered by updated_at DESC)
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-lg border border-gray-300"
                >
                  <Filter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSortDirection(current => current === 'asc' ? 'desc' : 'asc')}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-lg border border-gray-300"
                >
                  <ArrowUpDown className="w-5 h-5" />
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as any)}
                  className="w-full rounded-md border-gray-300"
                >
                  <option value="name">Sort by Name</option>
                  <option value="price">Sort by Price</option>
                  <option value="stock_level">Sort by Stock</option>
                </select>
              </div>
            )}
          </div>

          <div className="mt-6">
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 gap-4">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`bg-white rounded-lg border shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${
                      isDeletingProduct === product.id || isUpdatingStatus === product.id ? 'opacity-50' : ''
                    }`}
                    onClick={() => handleCardClick(product)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-20 h-20 flex-shrink-0">
                        <ImageWithFallback
                          src={product.images[0]?.url}
                          alt={product.name}
                          className="w-20 h-20 rounded-lg object-cover"
                          fallbackText={product.name}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="inventory-card-title text-sm font-medium text-gray-900">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500">{product.brand}</p>
                            <p className="text-sm font-medium text-blue-600">
                              ${product.price.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              Stock: {product.stock_level || product.stockLevel || 0}
                            </p>
                            <p className="text-sm text-gray-500">
                              Condition: {product.category}
                            </p>
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <ProductActions product={product} />
                          </div>
                        </div>
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              product.is_published
                                ? 'bg-green-100 text-green-800'
                                : product.is_draft
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {product.is_published ? 'Published' : product.is_draft ? 'Draft' : 'Unpublished'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stock
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Condition
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.map((product) => (
                      <tr 
                        key={product.id}
                        className={isDeletingProduct === product.id || isUpdatingStatus === product.id ? 'opacity-50' : ''}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <ImageWithFallback
                                src={product.images[0]?.url}
                                alt={product.name}
                                className="h-10 w-10 rounded-lg object-cover"
                                fallbackText={product.name}
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {product.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {product.brand}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${product.price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.stock_level || product.stockLevel || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.is_published
                                ? 'bg-green-100 text-green-800'
                                : product.is_draft
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {product.is_published ? 'Published' : product.is_draft ? 'Draft' : 'Unpublished'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <ProductActions product={product} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Product Dialog */}
      {selectedProduct && (
        <EditProductDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          onSave={handleSaveEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Delete Product</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{productToDelete.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setProductToDelete(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(productToDelete.id)}
                disabled={isDeletingProduct === productToDelete.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
              >
                {isDeletingProduct === productToDelete.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}